/**
 * Packages and pricing — the screen that decides what a devotee is charged.
 *
 * **Every amount here is an integer number of paise and nothing on this screen computes one.**
 * The line-item table shows the raw paise beside the formatted rupees precisely so an operator can
 * see there is no rounding happening between them, and the totals come from the server: a second
 * implementation of the pricing maths would be a second answer to what somebody owes.
 *
 * **Publishing is a versioned act, not an edit.** The flow is clone-to-draft → change the draft →
 * publish. The previous version is archived rather than overwritten, so a booking priced against
 * it still reproduces its invoice. A published sheet cannot be edited at all: the server answers
 * `409 version_frozen`, and this screen shows that refusal rather than pre-emptively greying
 * everything out — "the button did nothing" explains less than the server's own sentence.
 *
 * **The preview is the only way to see a city's price before publishing it.** An override changes
 * one row for one city; without a per-city dry run the consequence of publishing is invisible
 * until a devotee meets it.
 *
 * What this screen does not do: create a tier or edit inclusion lists — those live on the puja
 * catalogue's Tiers tab. Fee rules and per-zone travel rules are here, in `PricingRules.tsx`.
 */
import { useState } from 'react';
import { Card, Icon, Note, StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, DiffView, Money, ScopeAction, Section } from '../components/kit';
import { amountSource, changedOnly, priceVersionDiff } from './priceDiff';
import { formatInr } from '../lib/money';
import { useConsole } from '../app/console';
import { FeeRules, TravelRules } from './PricingRules';
import { QuotePreviewSection } from './QuotePreview';
import { ScopeOverrides, scopeLabel } from './PricingScopes';
import { errorMessage } from '../lib/api/client';
import { useAxisValues, usePujas } from '../lib/api/hooks/catalogue';
import { useCities } from '../lib/api/hooks/geo';
import type { CityOverrideIn, LineItemIn } from '../lib/api/hooks/pricing';
import {
  useCreateDraft,
  useRestoreVersion,
  useDiscardDraft,
  usePackages,
  usePriceVersion,
  usePriceVersions,
  usePricePreview,
  usePublishVersion,
  useReplaceCityOverrides,
  useReplaceLineItems,
  type PriceVersionDetail,
  type PriceVersionRow,
} from '../lib/api/hooks/pricing';

/** `draft` is the only editable state; the other two are history. */
function statusTone(status: string): 'warn' | 'success' | 'neutral' {
  if (status === 'draft') return 'warn';
  if (status === 'published') return 'success';
  return 'neutral';
}

/** The server's eight row types. Only `discount` may appear twice on one sheet. */
const ITEM_TYPES = [
  'pandit_fee',
  'samagri',
  'travel',
  'platform_fee',
  'temple_charge',
  'gst',
  'discount',
  'dakshina_guidance',
] as const;

/** What a line item's amount comes from, in the words the server uses. */
/** A switched-off puja says so in the picker, so nobody prices one thinking it is live. */
function pujaLabel(puja: { name_en: string; is_active: boolean }): string {
  return puja.is_active ? puja.name_en : `${puja.name_en} (switched off)`;
}

export function Pricing() {
  const { scope, toast } = useConsole();
  const [pujaId, setPujaId] = useState<number | undefined>();
  const [packageId, setPackageId] = useState<number | undefined>();
  const [versionId, setVersionId] = useState<number | undefined>();
  const [previewCityId, setPreviewCityId] = useState<number | undefined>();
  const [confirm, setConfirm] = useState<'diff' | 'publish' | 'discard' | null>(null);
  /** Local edits to the draft's amounts, keyed by line-item id. Nothing is sent until Save. */
  const [edits, setEdits] = useState<Record<number, string>>({});

  /* Switched-off pujas too: a puja is priced BEFORE it goes live, or switching it on publishes a
     page nobody can book. Archived ones stay out — the server leaves them out by default. */
  const pujas = usePujas({ page: 1, page_size: 200 });
  const packages = usePackages(pujaId);
  const versions = usePriceVersions(packageId);
  const version = usePriceVersion(versionId);
  const preview = usePricePreview(versionId, previewCityId);
  const cities = useCities({ page: 1, page_size: 200 });

  const createDraft = useCreateDraft();
  const restore = useRestoreVersion();
  const replaceItems = useReplaceLineItems();
  const replaceOverrides = useReplaceCityOverrides();
  /* Staged, like the amounts above: an override is a price somebody will be charged, and a
     control that wrote on every keystroke would send half-typed figures. */
  const [overrideDraft, setOverrideDraft] = useState<CityOverrideIn[] | null>(null);
  const [overrideCity, setOverrideCity] = useState('');
  const [overrideItem, setOverrideItem] = useState('');
  const [overridePaise, setOverridePaise] = useState('');
  const publish = usePublishVersion();
  const discard = useDiscardDraft();
  /* A new row is staged here and sent with the rest of the sheet: the endpoint is a replace-set,
     and the server — not this form — decides whether the row is legal. */
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newPaise, setNewPaise] = useState('');

  const outcome = (message: string, tone: 'success' | 'info' = 'success') => ({
    onSuccess: () => toast({ tone, message }),
    onError: (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) }),
  });

  const pujaRows = pujas.data?.items ?? [];
  const picked = pujaRows.find((p) => p.id === pujaId);
  const packageRows = packages.data?.items ?? [];
  const versionRows: PriceVersionRow[] = versions.data?.items ?? [];
  const openDraft = versionRows.find((v) => v.status === 'draft');
  const livePrice = versionRows.find((v) => v.status === 'published');
  const shown = version.data;
  const isDraft = shown?.status === 'draft';
  /* The live sheet, read only while a draft is open — it is the "before" of the review diff. */
  const live = usePriceVersion(isDraft ? livePrice?.id : undefined);
  /* The staged set, or the server's if nothing has been staged. `null` is what makes Save
     inert — an empty array is a legitimate edit meaning "remove every override". */
  const overrides: CityOverrideIn[] =
    overrideDraft ??
    (shown?.city_overrides ?? []).map((o) => ({
      city_id: o.city_id,
      item_type: o.item_type,
      amount_paise: o.amount_paise,
      note: o.note,
    }));
  const cityName = (id: number) =>
    cities.data?.items.find((c) => c.id === id)?.name ?? String(id);
  const languages = useAxisValues('ritual-languages').data?.items ?? [];
  const sampradayas = useAxisValues('sampradayas').data?.items ?? [];
  const diffRows =
    shown && live.data
      ? priceVersionDiff(live.data, shown, cityName, (o) => scopeLabel(o, languages, sampradayas))
      : null;
  const diffLabels = [`v${live.data?.version_number} live`, `v${shown?.version_number} draft`];
  /* A whole override, or null. Built here so the Add button is disabled until it resolves and
     the click handler needs no guard the UI cannot reach. */
  const addableOverride: CityOverrideIn | null = (() => {
    const city = cities.data?.items.find((c) => c.name === overrideCity);
    const item = shown?.line_items.find((i) => i.item_type === overrideItem);
    if (!city || !item || !/^\d+$/.test(overridePaise.trim())) return null;
    return {
      city_id: city.id,
      item_type: item.item_type,
      amount_paise: Number(overridePaise.trim()),
      note: null,
    };
  })();
  const dirty = Object.keys(edits).length > 0;

  /* Selecting a puja invalidates every narrower choice. Leaving a stale package id selected would
     show one puja's tiers under another puja's name — the kind of wrong that looks right. */
  const pickPuja = (id: number | undefined) => {
    setPujaId(id);
    setPackageId(undefined);
    setVersionId(undefined);
    setEdits({});
  };
  const pickPackage = (id: number) => {
    setPackageId(id);
    setVersionId(undefined);
    setEdits({});
    setOverrideDraft(null);
  };
  const pickVersion = (id: number) => {
    setVersionId(id);
    setEdits({});
    setOverrideDraft(null);
  };

  /* Takes the sheet rather than reading `shown`: the Save button only exists when a sheet is
     loaded, so a `if (!shown) return` guard here would be a branch no test could reach. */
  const sheetItems = (sheet: PriceVersionDetail): LineItemIn[] =>
    sheet.line_items.map((item) => ({
          item_type: item.item_type,
          label: item.label,
          label_hi: item.label_hi,
          amount_paise:
            edits[item.id] === undefined
              ? item.amount_paise
              : edits[item.id] === ''
                ? null
                : Number(edits[item.id]),
          fee_rule_id: item.fee_rule_id,
          is_travel_resolved: item.is_travel_resolved,
          is_taxable: item.is_taxable,
          hsn_sac_code: item.hsn_sac_code,
          is_optional: item.is_optional,
          sort_order: item.sort_order,
        }));

  const saveAmounts = (sheet: PriceVersionDetail) => {
    replaceItems.mutate(
      { versionId: sheet.id, items: sheetItems(sheet) },
      {
        onSuccess: () => {
          setEdits({});
          toast({ tone: 'success', message: 'Draft saved · devotees still see the live version' });
        },
        onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );
  };

  /* Blank paise is sent as no amount: a computed row (GST, travel) is resolved at quote time,
     and any other row without one is refused by the server in its own words. */
  const addLineItem = (sheet: PriceVersionDetail) => {
    replaceItems.mutate(
      {
        versionId: sheet.id,
        items: [
          ...sheetItems(sheet),
          {
            item_type: newType,
            label: newLabel.trim(),
            amount_paise: newPaise === '' ? null : Number(newPaise),
            is_travel_resolved: false,
            is_taxable: false,
            is_optional: false,
            sort_order: Math.min(999, Math.max(0, ...sheet.line_items.map((i) => i.sort_order)) + 1),
          },
        ],
      },
      {
        onSuccess: () => {
          setEdits({});
          setAdding(false);
          setNewType('');
          setNewLabel('');
          setNewPaise('');
          toast({ tone: 'success', message: 'Line item added · devotees still see the live version' });
        },
        onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-lg)', alignItems: 'start' }}>
      <Section title="Puja" note="Pricing is per tier of one puja">
        {pujas.isPending && <LoadingState message="Reading the catalogue…" />}
        {pujas.isError && (
          <ErrorState title="Could not read the catalogue" cause={errorMessage(pujas.error)} />
        )}
        {pujas.isSuccess && pujaRows.length === 0 && (
          <EmptyState icon="layers" title="No puja to price" inline />
        )}
        {pujaRows.length > 0 && (
          <Select
            label="Puja"
            placeholder="Choose a puja"
            value={picked ? pujaLabel(picked) : ''}
            options={pujaRows.map(pujaLabel)}
            onChange={(label) => pickPuja(pujaRows.find((p) => pujaLabel(p) === label)?.id)}
            style={{ maxWidth: 420 }}
          />
        )}
      </Section>

      {pujaId !== undefined && (
        <Section title="Tiers" note="What a devotee chooses between">
          {packages.isPending && <LoadingState message="Reading tiers…" />}
          {packages.isError && (
            <ErrorState title="Could not read tiers" cause={errorMessage(packages.error)} />
          )}
          {packages.isSuccess && packageRows.length === 0 && (
            <EmptyState
              icon="tag"
              title="This puja has no tier yet"
              body="A tier is created from the puja catalogue; it is priced here."
              inline
            />
          )}
          {packageRows.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 'var(--sp-md)',
                marginTop: 'var(--sp-md)',
              }}
            >
              {packageRows.map((p) => (
                <Card
                  key={p.id}
                  variant="flat"
                  padding="var(--sp-lg)"
                  onClick={() => pickPackage(p.id)}
                  style={{
                    display: 'grid',
                    gap: 'var(--sp-sm)',
                    borderColor: packageId === p.id ? 'var(--c-saffron)' : 'var(--c-border)',
                    background: packageId === p.id ? 'var(--c-saffron-50)' : 'var(--c-surface)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
                    <span style={{ fontWeight: 'var(--fw-semibold)' }}>{p.name}</span>
                    {/* Selected always adds a tick — colour never carries meaning alone. */}
                    {packageId === p.id && (
                      <Icon name="check" size={16} style={{ color: 'var(--c-saffron)' }} />
                    )}
                  </div>
                  <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                    {p.tier} · {p.duration_minutes} min · {p.pandit_count} pandit
                    {p.pandit_count === 1 ? '' : 's'} · {p.inclusions.length} inclusions
                  </span>
                </Card>
              ))}
            </div>
          )}
        </Section>
      )}

      {packageId !== undefined && (
        <Section
          title="Price versions"
          note="A published sheet is frozen; a change is a new version"
          pad={false}
          /* Offered once the versions are read: before that, the label would say "the first
             draft" about a tier that may already carry a published sheet. */
          actions={
            versions.isSuccess && (
              <ScopeAction
                scope={scope}
                need="catalogue_content"
                size="sm"
                icon="plus"
                onClick={() =>
                  createDraft.mutate(packageId, {
                    onSuccess: (draft: PriceVersionDetail) => {
                      setVersionId(draft.id);
                      toast({ tone: 'success', message: `Draft v${draft.version_number} started` });
                    },
                    onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
                  })
                }
              >
                {openDraft ? 'Start a draft' : livePrice ? 'Clone live to a draft' : 'Start the first draft'}
              </ScopeAction>
            )
          }
        >
          {versions.isPending && <LoadingState message="Reading versions…" />}
          {versions.isError && (
            <ErrorState title="Could not read versions" cause={errorMessage(versions.error)} />
          )}
          {versions.isSuccess && versionRows.length === 0 && (
            <EmptyState
              icon="tag"
              title="This tier has never been priced"
              body="Until a version is published the tier is visible and not bookable."
              inline
            />
          )}
          {versionRows.length > 0 && (
            <DataTable
              density="compact"
              columns={[
                { key: 'version', label: 'Version', mono: true },
                { key: 'status', label: 'State' },
                { key: 'total', label: 'Fixed total', align: 'right', mono: true },
                { key: 'note', label: 'Change' },
                { key: 'act', label: '' },
              ]}
              rows={versionRows.map((v) => ({
                id: v.id,
                version: `v${v.version_number}`,
                status: <StatusChip tone={statusTone(v.status)} label={v.status} />,
                total: <Money paise={v.base_total_paise} showPaise />,
                note: v.change_note ?? '—',
                act: (
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="ghost"
                    onClick={() => pickVersion(v.id)}
                  >
                    Open
                  </ScopeAction>
                ),
              }))}
            />
          )}
        </Section>
      )}

      {versionId !== undefined && (
        <>
          <Section
            title={shown ? `Line items · v${shown.version_number}` : 'Line items'}
            note="Every charge a devotee can see, in paise"
            pad={false}
            actions={
              isDraft ? (
                <>
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="ghost"
                    icon="plus"
                    disabled={adding}
                    onClick={() => setAdding(true)}
                  >
                    Add line item
                  </ScopeAction>
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="secondary"
                    disabled={!dirty}
                    onClick={() => saveAmounts(shown!)}
                  >
                    Save draft
                  </ScopeAction>
                  {livePrice && (
                    <ScopeAction
                      scope={scope}
                      need="finance"
                      size="sm"
                      variant="secondary"
                      icon="git-compare"
                      disabled={diffRows === null}
                      onClick={() => setConfirm('diff')}
                    >
                      Review diff
                    </ScopeAction>
                  )}
                  <ScopeAction
                    scope={scope}
                    need="finance"
                    size="sm"
                    icon="upload"
                    onClick={() => setConfirm('publish')}
                  >
                    Publish
                  </ScopeAction>
                </>
              ) : shown?.status === 'archived' && packageId !== undefined ? (
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  variant="secondary"
                  icon="rotate-ccw"
                  /* One open draft per tier: the server refuses a second, so the button says so first. */
                  disabled={openDraft !== undefined}
                  onClick={() =>
                    restore.mutate(
                      { packageId, fromVersionId: shown.id },
                      {
                        onSuccess: (draft: PriceVersionDetail) => {
                          setVersionId(draft.id);
                          toast({
                            tone: 'success',
                            message: `v${shown.version_number}'s prices are in draft v${draft.version_number} — publish to make them live`,
                          });
                        },
                        onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
                      },
                    )
                  }
                >
                  Restore these prices
                </ScopeAction>
              ) : undefined
            }
          >
            {version.isPending && <LoadingState message="Reading the sheet…" />}
            {version.isError && (
              <ErrorState title="Could not read the sheet" cause={errorMessage(version.error)} />
            )}
            {shown && (
              <>
                {dirty && (
                  <div
                    style={{
                      padding: 'var(--sp-md) var(--sp-lg)',
                      color: 'var(--c-warn-text)',
                      background: 'var(--c-warn-bg)',
                      fontSize: 'var(--fs-admin-ui)',
                    }}
                  >
                    Unsaved — a devotee still sees the live version.
                  </div>
                )}
                {!isDraft && (
                  <div
                    style={{
                      padding: 'var(--sp-md) var(--sp-lg)',
                      color: 'var(--c-ink2)',
                      background: 'var(--c-surface2)',
                      fontSize: 'var(--fs-admin-ui)',
                    }}
                  >
                    v{shown.version_number} is {shown.status} and frozen. Bookings priced against it
                    reproduce their invoice from it — clone it to a draft to change anything.
                  </div>
                )}
                <DataTable
                  density="compact"
                  columns={[
                    { key: 'item', label: 'Line item' },
                    { key: 'source', label: 'Source' },
                    { key: 'paise', label: 'Paise', align: 'right', mono: true },
                    { key: 'amount', label: 'Amount', align: 'right', mono: true },
                  ]}
                  rows={shown.line_items.map((item) => {
                    const raw =
                      edits[item.id] ??
                      (item.amount_paise === null || item.amount_paise === undefined
                        ? ''
                        : String(item.amount_paise));
                    return {
                      id: item.id,
                      item: (
                        <span>
                          {item.label}
                          {item.is_optional && (
                            <span style={{ color: 'var(--c-ink3)' }}> · optional</span>
                          )}
                        </span>
                      ),
                      source: (
                        <span style={{ color: 'var(--c-ink3)' }}>{amountSource(item)}</span>
                      ),
                      paise: isDraft ? (
                        <Input
                          label={`${item.label} in paise`}
                          inputMode="numeric"
                          value={raw}
                          onChange={(next) =>
                            setEdits((prev) => ({ ...prev, [item.id]: next.replace(/[^0-9]/g, '') }))
                          }
                          style={{ maxWidth: 140 }}
                        />
                      ) : (
                        <span style={{ color: 'var(--c-ink3)' }}>{raw === '' ? '—' : raw}</span>
                      ),
                      amount:
                        raw === '' ? (
                          <span style={{ color: 'var(--c-ink3)' }}>at quote time</span>
                        ) : (
                          <Money paise={Number(raw)} showPaise />
                        ),
                    };
                  })}
                  footer={
                    <>
                      <span>Fixed total, as the server computed it</span>
                      <span className="mono-num tabular">
                        {formatInr(shown.base_total_paise, { paise: true })}
                      </span>
                    </>
                  }
                />
                {isDraft && adding && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--sp-md)',
                      alignItems: 'end',
                      padding: 'var(--sp-md) var(--sp-lg)',
                    }}
                  >
                    <Select
                      density="admin"
                      label="Item type"
                      options={ITEM_TYPES.filter(
                        (t) => t === 'discount' || !shown.line_items.some((i) => i.item_type === t),
                      )}
                      value={newType}
                      onChange={setNewType}
                      style={{ flex: 1 }}
                    />
                    <Input
                      density="admin"
                      label="Label"
                      value={newLabel}
                      onChange={setNewLabel}
                      style={{ flex: 2 }}
                    />
                    <Input
                      density="admin"
                      label="Amount in paise"
                      inputMode="numeric"
                      value={newPaise}
                      onChange={(next) => setNewPaise(next.replace(/[^0-9]/g, ''))}
                      placeholder="blank = at quote time"
                    />
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      disabled={newType === '' || newLabel.trim() === ''}
                      onClick={() => addLineItem(shown)}
                    >
                      Add to draft
                    </ScopeAction>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      onClick={() => setAdding(false)}
                    >
                      Cancel
                    </ScopeAction>
                  </div>
                )}
              </>
            )}
          </Section>

          <Section
            title="What this sheet resolves to"
            note="Choose a city to apply its overrides"
            pad={false}
            actions={
              cities.data && cities.data.items.length > 0 ? (
                <Select
                  ariaLabel="Preview city"
                  placeholder="Version defaults"
                  value={cities.data.items.find((c) => c.id === previewCityId)?.name ?? ''}
                  options={cities.data.items.map((c) => c.name)}
                  onChange={(name) =>
                    setPreviewCityId(cities.data!.items.find((c) => c.name === name)?.id)
                  }
                  style={{ minWidth: 200 }}
                />
              ) : undefined
            }
          >
            {preview.isPending && <LoadingState message="Resolving…" />}
            {preview.isError && (
              <ErrorState title="Could not resolve this sheet" cause={errorMessage(preview.error)} />
            )}
            {preview.data && (
              <DataTable
                density="compact"
                columns={[
                  { key: 'item', label: 'Line item' },
                  { key: 'source', label: 'From' },
                  { key: 'amount', label: 'Amount', align: 'right', mono: true },
                ]}
                rows={preview.data.lines.map((line) => ({
                  id: `${line.item_type}-${line.sort_order}`,
                  item: line.label,
                  source:
                    line.source === 'city_override' ? (
                      <StatusChip tone="info" label="city override" />
                    ) : (
                      <span style={{ color: 'var(--c-ink3)' }}>{line.source}</span>
                    ),
                  amount:
                    line.amount_paise === null || line.amount_paise === undefined ? (
                      <span style={{ color: 'var(--c-ink3)' }}>needs an address</span>
                    ) : (
                      <Money paise={line.amount_paise} showPaise />
                    ),
                }))}
                footer={
                  <>
                    {/* Indicative, and said so: travel and tax need an address and a date. A
                        headline that quietly omits them is a number a devotee is surprised by. */}
                    <span>
                      Indicative total —{' '}
                      {preview.data.city_resolved ? 'for this city' : 'version defaults'}, before
                      travel and tax
                    </span>
                    <span className="mono-num tabular">
                      {formatInr(preview.data.indicative_total_paise, { paise: true })}
                    </span>
                  </>
                }
              />
            )}
          </Section>

          {previewCityId !== undefined && (
            <QuotePreviewSection
              key={previewCityId}
              versionId={versionId}
              cityId={previewCityId}
              cityName={cities.data!.items.find((c) => c.id === previewCityId)!.name}
            />
          )}

          {isDraft && (
            <Section
              title="Per-city amounts"
              note="One row, one city — everything else falls back to the version's own figure"
            >
              <Note>
                An override changes what a devotee in that city is charged for one line item. The
                preview above is the only way to see the consequence before publishing, so read it
                against the city you just changed.
              </Note>
              {overrides.length === 0 && (
                <EmptyState
                  icon="map-pin"
                  title="No city overrides"
                  body="Every city is charged the version's own amounts."
                  inline
                />
              )}
              {overrides.map((o, index) => (
                <div
                  key={`${o.city_id}-${o.item_type}`}
                  style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'center' }}
                >
                  <span style={{ flex: 1 }}>
                    {cityName(o.city_id)} · {o.item_type}
                  </span>
                  {/* Raw paise beside the rupees, for the reason the line-item table does it:
                      so an operator can see nothing is rounding between the two. */}
                  <span className="mono-num tabular">{o.amount_paise}</span>
                  <Money paise={o.amount_paise} showPaise />
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="ghost"
                    onClick={() => setOverrideDraft(overrides.filter((_, i) => i !== index))}
                  >
                    Remove
                  </ScopeAction>
                </div>
              ))}
              {cities.data && cities.data.items.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--sp-md)',
                    alignItems: 'end',
                    marginTop: 'var(--sp-md)',
                  }}
                >
                  <Select
                    density="admin"
                    label="City"
                    options={cities.data.items.map((c) => c.name)}
                    value={overrideCity}
                    onChange={setOverrideCity}
                    style={{ flex: 1 }}
                  />
                  <Select
                    density="admin"
                    label="Line item"
                    options={shown.line_items.map((i) => i.item_type)}
                    value={overrideItem}
                    onChange={setOverrideItem}
                    style={{ flex: 1 }}
                  />
                  <Input
                    density="admin"
                    label="Paise"
                    inputMode="numeric"
                    value={overridePaise}
                    onChange={setOverridePaise}
                    placeholder="339900"
                  />
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="secondary"
                    disabled={!addableOverride}
                    onClick={() => {
                      setOverrideDraft([
                        ...overrides.filter(
                          (o) => !(o.city_id === addableOverride!.city_id && o.item_type === addableOverride!.item_type),
                        ),
                        addableOverride!,
                      ]);
                      setOverridePaise('');
                    }}
                  >
                    Add override
                  </ScopeAction>
                </div>
              )}
              <div style={{ marginTop: 'var(--sp-md)' }}>
                <ScopeAction
                  scope={scope}
                  need="catalogue_content"
                  size="sm"
                  disabled={overrideDraft === null}
                  onClick={() =>
                    replaceOverrides.mutate(
                      { versionId: shown.id, items: overrides },
                      {
                        onSuccess: () => {
                          setOverrideDraft(null);
                          toast({ tone: 'success', message: 'Per-city amounts saved' });
                        },
                        onError: (e: unknown) =>
                          toast({ tone: 'error', message: errorMessage(e) }),
                      },
                    )
                  }
                >
                  Save per-city amounts
                </ScopeAction>
              </div>
            </Section>
          )}

          {isDraft && <ScopeOverrides key={shown.id} sheet={shown} />}

          {isDraft && (
            <Section title="Discard" note="A draft was never charged against, so nothing is lost">
              <ScopeAction
                scope={scope}
                need="catalogue_content"
                size="sm"
                variant="secondary"
                icon="trash-2"
                onClick={() => setConfirm('discard')}
              >
                Discard this draft
              </ScopeAction>
            </Section>
          )}
        </>
      )}

      <FeeRules />
      <TravelRules />

      <ConfirmAction
        open={confirm === 'diff'}
        title={`${diffLabels[0]} → ${diffLabels[1]}`}
        phrase="REVIEWED"
        tone="warn"
        consequences={[
          'This is a review step only — nothing changes when you close it.',
          'The changed rows are shown again, with type-to-confirm, at publish.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={() => setConfirm('publish')}
        confirmLabel="Continue to publish"
      >
        {diffRows && <DiffView labels={diffLabels} rows={diffRows} />}
      </ConfirmAction>

      <ConfirmAction
        open={confirm === 'publish'}
        title={shown ? `Publish price version v${shown.version_number}?` : 'Publish?'}
        phrase="PUBLISH"
        tone="error"
        reason="What changed, and why"
        consequences={[
          'This becomes what devotees are charged, from the next booking onward.',
          livePrice
            ? `v${livePrice.version_number} is archived, not deleted — bookings priced against it are unaffected.`
            : 'This tier becomes bookable for the first time.',
          'Open quotes referencing the archived version are refused and re-quoted.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={(reason) => {
          setConfirm(null);
          publish.mutate(
            { versionId: versionId!, changeNote: reason },
            outcome('Published · this is now the live price'),
          );
        }}
        confirmLabel="Publish"
      >
        {diffRows && changedOnly(diffRows).length > 0 && (
          <DiffView labels={diffLabels} rows={changedOnly(diffRows)} />
        )}
      </ConfirmAction>

      <ConfirmAction
        open={confirm === 'discard'}
        title="Discard this draft?"
        phrase="DISCARD"
        tone="warn"
        consequences={[
          'The draft and every unsaved amount in it are deleted.',
          'The live version is untouched — devotees see no change.',
        ]}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          discard.mutate(versionId!, {
            onSuccess: () => {
              setVersionId(undefined);
              toast({ tone: 'info', message: 'Draft discarded' });
            },
            onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }),
          });
        }}
        confirmLabel="Discard"
      />
    </div>
  );
}
